/**
 * Persist enrichment identity in localStorage so the session is shared across
 * all tabs from the same origin. Access to enrichment APIs requires a short-lived
 * JWT from POST /enrichment/verify-otp (stored alongside entity id and email).
 */

const KEY_ENTITY_ID = "enrichment_public_entity_id";
const KEY_EMAIL = "enrichment_email";
const KEY_INTERESTS = "enrichment_interests";
const KEY_ACCESS_TOKEN = "enrichment_access_token";

export interface StoredInterests {
	state: string;
	county: string;
}

export function getEnrichmentEntityId(): string | null {
	if (typeof window === "undefined") return null;
	return localStorage.getItem(KEY_ENTITY_ID);
}

export function getEnrichmentEmail(): string | null {
	if (typeof window === "undefined") return null;
	return localStorage.getItem(KEY_EMAIL);
}

export function getEnrichmentAccessToken(): string | null {
	if (typeof window === "undefined") return null;
	return localStorage.getItem(KEY_ACCESS_TOKEN);
}

export function getEnrichmentInterests(): StoredInterests[] | null {
	if (typeof window === "undefined") return null;
	try {
		const raw = localStorage.getItem(KEY_INTERESTS);
		if (!raw) return null;
		return JSON.parse(raw) as StoredInterests[];
	} catch {
		return null;
	}
}

export function setEnrichmentSession(
	enrichmentPublicEntityId: string,
	email: string,
	accessToken: string,
	interests?: StoredInterests[]
): void {
	if (typeof window === "undefined") return;
	localStorage.setItem(KEY_ENTITY_ID, enrichmentPublicEntityId);
	localStorage.setItem(KEY_EMAIL, email);
	localStorage.setItem(KEY_ACCESS_TOKEN, accessToken);
	if (interests != null) {
		localStorage.setItem(KEY_INTERESTS, JSON.stringify(interests));
	} else {
		localStorage.removeItem(KEY_INTERESTS);
	}
}

export function clearEnrichmentSession(): void {
	if (typeof window === "undefined") return;
	localStorage.removeItem(KEY_ENTITY_ID);
	localStorage.removeItem(KEY_EMAIL);
	localStorage.removeItem(KEY_INTERESTS);
	localStorage.removeItem(KEY_ACCESS_TOKEN);
}

export function hasEnrichmentSession(): boolean {
	if (typeof window === "undefined") return false;
	return !!(
		localStorage.getItem(KEY_ACCESS_TOKEN) &&
		localStorage.getItem(KEY_ENTITY_ID)
	);
}
